import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from '../../api/axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const KukoList = () => {
  const navigate = useNavigate();

  // State management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterInterest, setFilterInterest] = useState('all');
  const [filterTaste, setFilterTaste] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [deleteBtnLoading, setDeleteBtnLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('delhi-event'); // 'delhi-event' or 'website-contacts'

  // Fetch data
  const fetchDelhiEventData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/delhi-event-data');

      if (response?.data?.data?.DelhiEventDataEntries) {
        setData(response.data.data.DelhiEventDataEntries);
      } else {
        throw new Error('Invalid data format received');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch data');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDelhiEventData();
  }, [fetchDelhiEventData]);

  // Memoized filtered data
  const filteredData = useMemo(() => {
    let result = data;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item =>
        item.name?.toLowerCase().includes(term) ||
        item.email?.toLowerCase().includes(term) ||
        item.mobile?.includes(searchTerm) ||
        item.city?.toLowerCase().includes(term) ||
        item.state?.toLowerCase().includes(term) ||
        item.pincode?.includes(searchTerm)
      );
    }

    if (filterStatus !== 'all') {
      result = result.filter(item =>
        filterStatus === 'verified' ? item.is_verified === 1 : item.is_verified === 0
      );
    }

    if (filterInterest !== 'all') {
      result = result.filter(item => item.interested_in === filterInterest);
    }

    if (filterTaste !== 'all') {
      result = result.filter(item => item.is_Kokum_Tasted_Before === filterTaste);
    }

    return result;
  }, [data, searchTerm, filterStatus, filterInterest, filterTaste]);

  // Memoized sorted data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'created_at' || sortConfig.key === 'updated_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterInterest, filterTaste]);

  // Sorting handler
  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Row selection handlers
  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedRows.size === currentItems.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(currentItems.map(item => item.id)));
    }
  };

  // Bulk actions
  const handleBulkAction = async () => {
    if (!bulkAction || selectedRows.size === 0) return;

    try {
      setLoading(true);

      switch (bulkAction) {
        case 'verify':
          await axios.post('/bulk-verify', { ids: Array.from(selectedRows) });
          toast.success(`${selectedRows.size} entries verified successfully!`);
          break;
        case 'delete':
          if (window.confirm(`Delete ${selectedRows.size} entries?`)) {
            await axios.post('/bulk-delete', { ids: Array.from(selectedRows) });
            toast.success(`${selectedRows.size} entries deleted successfully!`);
          }
          break;
        case 'export':
          exportSelectedData();
          return;
        default:
          return;
      }

      await fetchDelhiEventData();
      setSelectedRows(new Set());
      setBulkAction('');
    } catch (err) {
      toast.error('Bulk action failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Export functionality
  const exportSelectedData = () => {
    const dataToExport = selectedRows.size > 0
      ? data.filter(item => selectedRows.has(item.id))
      : sortedData;

    if (dataToExport.length === 0) {
      toast.warning('No data available to export.');
      return;
    }

    const headers = [
      'Name', 'Email', 'Mobile', 'City', 'State', 'Pincode',
      'Interest', 'Tasted Before', 'Status', 'Date Added'
    ];

    const csvContent = [
      headers.join(','),
      ...dataToExport.map(item =>
        [
          `"${item.name || 'N/A'}"`,
          `"${item.email || 'N/A'}"`,
          `"${item.mobile || 'N/A'}"`,
          `"${item.city || 'N/A'}"`,
          `"${item.state || 'N/A'}"`,
          `"${item.pincode || 'N/A'}"`,
          `"${item.interested_in || 'N/A'}"`,
          `"${item.is_Kokum_Tasted_Before || 'N/A'}"`,
          `"${item.is_verified ? 'Verified' : 'Unverified'}"`,
          `"${formatDate(item.created_at)}"`
        ].join(',')
      )
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `delhi-event-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast.success(`${dataToExport.length} records exported!`);
  };

  // Delete handlers
  const handleOpenDeleteModal = (entry) => {
    setSelectedEntry(entry);
    setOpenDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedEntry) return;

    setDeleteBtnLoading(true);
    try {
      // Add your delete API endpoint here
      await axios.delete(`/delhi-event-data/${selectedEntry.id}`);
      toast.success('Entry deleted successfully!');
      await fetchDelhiEventData();
      setOpenDeleteModal(false);
      setSelectedEntry(null);
    } catch (error) {
      toast.error('Failed to delete entry. Please try again.');
    } finally {
      setDeleteBtnLoading(false);
    }
  };

  // Utility functions
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (isVerified) => {
    return {
      backgroundColor: isVerified ? '#d1fae5' : '#fef3c7',
      color: isVerified ? '#065f46' : '#92400e',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '0.75rem',
      fontWeight: '500',
      display: 'inline-block'
    };
  };

  const getInterestBadge = (interest) => {
    const styles = {
      'DISTRIBUTION': { bg: '#e3f2fd', color: '#0d47a1', label: 'Distribution' },
      'CONSUMER': { bg: '#e8f5e8', color: '#1b5e20', label: 'Consumer' },
      'MEDIA': { bg: '#fff3e0', color: '#e65100', label: 'Media' }
    };
    const style = styles[interest] || { bg: '#f5f5f5', color: '#616161', label: interest };

    return {
      backgroundColor: style.bg,
      color: style.color,
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '0.75rem',
      fontWeight: '500',
      display: 'inline-block'
    };
  };

  const getTasteBadge = (tasted) => {
    const styles = {
      'YES': { bg: '#d1fae5', color: '#065f46', label: 'Tasted' },
      'NO': { bg: '#f3f4f6', color: '#4b5563', label: 'Not Tasted' }
    };
    const style = styles[tasted] || { bg: '#fef3c7', color: '#92400e', label: tasted || 'Unknown' };

    return {
      backgroundColor: style.bg,
      color: style.color,
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '0.75rem',
      fontWeight: '500',
      display: 'inline-block'
    };
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    navigate('/');
    toast.info('Logged out successfully');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'delhi-event') {
      navigate('/listing');
    } else {
      navigate('/enquiry-listing');
    }
  };

  // Loading state
  if (loading && data.length === 0) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner}></div>
        <p style={styles.loadingText}>Loading Delhi Event Data...</p>
      </div>
    );
  }

  // Error state
  if (error && data.length === 0) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorIcon}>⚠️</div>
        <h5 style={styles.errorTitle}>Error Loading Data</h5>
        <p style={styles.errorMessage}>{error}</p>
        <button onClick={fetchDelhiEventData} style={styles.retryButton}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header with Tabs */}
      <div style={styles.header}>
        <div>
          <div style={styles.tabContainer}>
            <button
              onClick={() => handleTabChange('delhi-event')}
              style={{
                ...styles.tab,
                ...(activeTab === 'delhi-event' ? styles.activeTab : {})
              }}
            >
              Delhi Event Enquiry
            </button>
            <button
              onClick={() => handleTabChange('website-contacts')}
              style={{
                ...styles.tab,
                ...(activeTab === 'website-contacts' ? styles.activeTab : {})
              }}
            >
              Website Contacts
            </button>
          </div>
          <p style={styles.subtitle}>
            <span style={styles.statItem}>{data.length} total entries</span>
            <span style={styles.statDivider}>•</span>
            <span style={styles.statItem}>{sortedData.length} filtered</span>
            {selectedRows.size > 0 && (
              <>
                <span style={styles.statDivider}>•</span>
                <span style={{ ...styles.statItem, color: '#f59e0b' }}>{selectedRows.size} selected</span>
              </>
            )}
          </p>
        </div>
        <div style={styles.headerActions}>
          <button onClick={exportSelectedData} style={styles.exportButton}>
            Export {selectedRows.size > 0 ? `(${selectedRows.size})` : ''}
          </button>
          <button onClick={fetchDelhiEventData} style={styles.refreshButton}>
            Refresh
          </button>
          <button onClick={handleLogout} style={styles.logoutButton}>
            Logout
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
        {[
          {
            label: 'Total Entries',
            value: data.length,
            color: '#3b82f6',
            icon: '📋'
          },
          {
            label: 'Verified',
            value: data.filter(i => i.is_verified === 1).length,
            color: '#10b981',
            icon: '✅'
          },
          {
            label: 'Unverified',
            value: data.filter(i => i.is_verified === 0).length,
            color: '#f59e0b',
            icon: '⏳'
          },
          {
            label: 'Distribution',
            value: data.filter(i => i.interested_in === 'DISTRIBUTION').length,
            color: '#8b5cf6',
            icon: '🏢'
          },
          {
            label: 'Consumer',
            value: data.filter(i => i.interested_in === 'CONSUMER').length,
            color: '#ec4899',
            icon: '👤'
          },
          {
            label: 'Tasted',
            value: data.filter(i => i.is_Kokum_Tasted_Before === 'YES').length,
            color: '#14b8a6',
            icon: '👅'
          }
        ].map((stat, index) => (
          <div key={index} style={styles.statCard}>
            <div style={styles.statCardContent}>
              <div style={{ ...styles.statIconContainer, backgroundColor: stat.color }}>
                <span style={styles.statIcon}>{stat.icon}</span>
              </div>
              <div>
                <div style={styles.statValue}>{stat.value}</div>
                <div style={styles.statLabel}>{stat.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={styles.filtersSection}>
        <div style={styles.filtersRow}>
          <div style={styles.searchBox}>
            <input
              type="text"
              placeholder="Search by name, email, mobile, city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} style={styles.clearSearch}>
                ×
              </button>
            )}
          </div>

          <select
            value={filterInterest}
            onChange={(e) => setFilterInterest(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">All Interests</option>
            <option value="DISTRIBUTION">Distribution</option>
            <option value="CONSUMER">Consumer</option>
            <option value="MEDIA">Media</option>
          </select>

          <select
            value={filterTaste}
            onChange={(e) => setFilterTaste(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">All Taste</option>
            <option value="YES">Tasted</option>
            <option value="NO">Not Tasted</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">All Status</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
          </select>

          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            style={styles.perPageSelect}
          >
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select>
        </div>

        {/* Active Filters */}
        {(searchTerm || filterInterest !== 'all' || filterTaste !== 'all' || filterStatus !== 'all') && (
          <div style={styles.activeFilters}>
            <span style={styles.activeFiltersLabel}>Active filters:</span>
            {searchTerm && (
              <span style={styles.filterBadge}>
                "{searchTerm}"
                <button onClick={() => setSearchTerm('')} style={styles.removeFilter}>×</button>
              </span>
            )}
            {filterInterest !== 'all' && (
              <span style={{ ...styles.filterBadge, backgroundColor: '#e3f2fd', color: '#0d47a1' }}>
                Interest: {filterInterest}
                <button onClick={() => setFilterInterest('all')} style={styles.removeFilter}>×</button>
              </span>
            )}
            {filterTaste !== 'all' && (
              <span style={{ ...styles.filterBadge, backgroundColor: '#d1fae5', color: '#065f46' }}>
                Taste: {filterTaste}
                <button onClick={() => setFilterTaste('all')} style={styles.removeFilter}>×</button>
              </span>
            )}
            {filterStatus !== 'all' && (
              <span style={{ ...styles.filterBadge, backgroundColor: '#fef3c7', color: '#92400e' }}>
                Status: {filterStatus}
                <button onClick={() => setFilterStatus('all')} style={styles.removeFilter}>×</button>
              </span>
            )}
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterInterest('all');
                setFilterTaste('all');
                setFilterStatus('all');
              }}
              style={styles.clearAllButton}
            >
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedRows.size > 0 && (
        <div style={styles.bulkActions}>
          <span style={styles.bulkSelected}>{selectedRows.size} entries selected</span>
          <select
            value={bulkAction}
            onChange={(e) => setBulkAction(e.target.value)}
            style={styles.bulkSelect}
          >
            <option value="">Bulk Actions</option>
            <option value="verify">Mark as Verified</option>
            <option value="delete">Delete Selected</option>
            <option value="export">Export Selected</option>
          </select>
          <button
            onClick={handleBulkAction}
            disabled={!bulkAction}
            style={styles.bulkApplyButton}
          >
            Apply
          </button>
        </div>
      )}

      {/* Table */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead style={styles.tableHead}>
            <tr>
              <th style={{ ...styles.tableHeader, width: '30px' }}>
                <input
                  type="checkbox"
                  checked={selectedRows.size === currentItems.length && currentItems.length > 0}
                  onChange={handleSelectAll}
                  disabled={currentItems.length === 0}
                  style={styles.checkbox}
                />
              </th>
              <th style={styles.tableHeader} onClick={() => handleSort('name')}>
                Name & Email {getSortIndicator('name')}
              </th>
              <th style={styles.tableHeader} onClick={() => handleSort('mobile')}>
                Mobile {getSortIndicator('mobile')}
              </th>
              <th style={styles.tableHeader} onClick={() => handleSort('city')}>
                Location {getSortIndicator('city')}
              </th>
              <th style={styles.tableHeader} onClick={() => handleSort('interested_in')}>
                Interest {getSortIndicator('interested_in')}
              </th>
              <th style={styles.tableHeader} onClick={() => handleSort('is_Kokum_Tasted_Before')}>
                Tasted {getSortIndicator('is_Kokum_Tasted_Before')}
              </th>
              <th style={styles.tableHeader} onClick={() => handleSort('is_verified')}>
                Status {getSortIndicator('is_verified')}
              </th>
              <th style={styles.tableHeader} onClick={() => handleSort('created_at')}>
                Date {getSortIndicator('created_at')}
              </th>
              <th style={{ ...styles.tableHeader, textAlign: 'center' }}>
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((item) => (
                <tr key={item.id} style={styles.tableRow}>
                  <td style={styles.tableCell}>
                    <input
                      type="checkbox"
                      checked={selectedRows.has(item.id)}
                      onChange={() => handleSelectRow(item.id)}
                      style={styles.checkbox}
                    />
                  </td>
                  <td style={styles.tableCell}>
                    <div style={styles.contactInfo}>
                      <div style={styles.contactName}>{item.name}</div>
                      <div style={styles.contactEmail}>{item.email}</div>
                    </div>
                  </td>
                  <td style={styles.tableCell}>
                    <span style={styles.mobileText}>{item.mobile}</span>
                  </td>
                  <td style={styles.tableCell}>
                    <div>
                      <div>{item.city}, {item.state}</div>
                      <div style={styles.pincodeText}>PIN: {item.pincode}</div>
                    </div>
                  </td>
                  <td style={styles.tableCell}>
                    <span style={getInterestBadge(item.interested_in)}>{item.interested_in}</span>
                  </td>
                  <td style={styles.tableCell}>
                    <span style={getTasteBadge(item.is_Kokum_Tasted_Before)}>
                      {item.is_Kokum_Tasted_Before || 'Unknown'}
                    </span>
                  </td>
                  <td style={styles.tableCell}>
                    <span style={getStatusBadge(item.is_verified)}>
                      {item.is_verified ? 'Verified' : 'Unverified'}
                    </span>
                  </td>
                  <td style={styles.tableCell}>
                    <span style={styles.dateText}>{formatDate(item.created_at)}</span>
                  </td>
                  <td style={{ ...styles.tableCell, textAlign: 'center' }}>
                    <button
                      onClick={() => handleOpenDeleteModal(item)}
                      style={styles.deleteButton}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" style={styles.emptyState}>
                  <div style={styles.emptyStateContent}>
                    <div style={styles.emptyIcon}>📭</div>
                    <p style={styles.emptyText}>No entries found</p>
                    {(searchTerm || filterInterest !== 'all' || filterTaste !== 'all' || filterStatus !== 'all') && (
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setFilterInterest('all');
                          setFilterTaste('all');
                          setFilterStatus('all');
                        }}
                        style={styles.clearFiltersButton}
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {sortedData.length > 0 && (
        <div style={styles.pagination}>
          <div style={styles.paginationInfo}>
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedData.length)} of {sortedData.length}
            {sortedData.length !== data.length && ` (filtered from ${data.length})`}
          </div>
          <div style={styles.paginationControls}>
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={styles.paginationButton}
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={styles.paginationButton}
            >
              Previous
            </button>
            <span style={styles.pageIndicator}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              style={styles.paginationButton}
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              style={styles.paginationButton}
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {openDeleteModal && selectedEntry && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Confirm Delete</h3>
              <button
                onClick={() => {
                  setOpenDeleteModal(false);
                  setSelectedEntry(null);
                }}
                style={styles.modalClose}
              >
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              <p style={styles.modalText}>
                Are you sure you want to delete <strong>{selectedEntry.name}</strong>'s entry?
              </p>
              <p style={styles.modalWarning}>This action cannot be undone.</p>
            </div>
            <div style={styles.modalFooter}>
              <button
                onClick={() => {
                  setOpenDeleteModal(false);
                  setSelectedEntry(null);
                }}
                style={styles.modalCancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteBtnLoading}
                style={styles.modalDeleteButton}
              >
                {deleteBtnLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const styles = {
  container: {
    padding: '24px',
    backgroundColor: '#f3f4f6',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px'
  },
  tabContainer: {
    display: 'flex',
    gap: '4px',
    marginBottom: '8px',
    borderBottom: '2px solid #e5e7eb'
  },
  tab: {
    padding: '10px 20px',
    fontSize: '15px',
    fontWeight: '500',
    color: '#6b7280',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  activeTab: {
    color: '#3b82f6',
    borderBottom: '2px solid #3b82f6',
    fontWeight: '600'
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0
  },
  statItem: {
    marginRight: '8px'
  },
  statDivider: {
    marginRight: '8px',
    color: '#d1d5db'
  },
  headerActions: {
    display: 'flex',
    gap: '8px'
  },
  exportButton: {
    padding: '8px 16px',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  refreshButton: {
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  logoutButton: {
    padding: '8px 16px',
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '16px',
    marginBottom: '24px'
  },
  statCard: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  statCardContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  statDot: {
    width: '40px',
    height: '40px',
    borderRadius: '8px'
  },
  statValue: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827',
    lineHeight: '1.2'
  },
  statLabel: {
    fontSize: '13px',
    color: '#6b7280'
  },
  filtersSection: {
    marginBottom: '16px'
  },
  filtersRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px'
  },
  searchBox: {
    flex: 2,
    position: 'relative'
  },
  searchInput: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box'
  },
  clearSearch: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    fontSize: '18px',
    color: '#9ca3af',
    cursor: 'pointer',
    padding: '0'
  },
  filterSelect: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    backgroundColor: 'white',
    cursor: 'pointer'
  },
  perPageSelect: {
    width: '120px',
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    backgroundColor: 'white',
    cursor: 'pointer'
  },
  activeFilters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    alignItems: 'center'
  },
  activeFiltersLabel: {
    fontSize: '13px',
    color: '#6b7280',
    marginRight: '4px'
  },
  filterBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    backgroundColor: '#e5e7eb',
    color: '#374151',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500'
  },
  removeFilter: {
    background: 'none',
    border: 'none',
    color: 'inherit',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '0 4px',
    opacity: 0.7
  },
  clearAllButton: {
    padding: '4px 12px',
    backgroundColor: 'transparent',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#6b7280',
    cursor: 'pointer'
  },
  bulkActions: {
    backgroundColor: '#fef3c7',
    border: '1px solid #fde68a',
    borderRadius: '6px',
    padding: '12px 16px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  bulkSelected: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#92400e'
  },
  bulkSelect: {
    padding: '6px 12px',
    border: '1px solid #fde68a',
    borderRadius: '4px',
    fontSize: '13px',
    outline: 'none',
    backgroundColor: 'white',
    minWidth: '160px'
  },
  bulkApplyButton: {
    padding: '6px 16px',
    backgroundColor: '#f59e0b',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  tableHead: {
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb'
  },
  tableHeader: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '500',
    color: '#374151',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap'
  },
  tableRow: {
    borderBottom: '1px solid #f3f4f6'
  },
  tableCell: {
    padding: '16px',
    color: '#111827'
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    margin: 0
  },
  contactInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  contactName: {
    fontWeight: '500',
    color: '#111827'
  },
  contactEmail: {
    fontSize: '12px',
    color: '#6b7280'
  },
  mobileText: {
    fontWeight: '500',
    color: '#111827'
  },
  pincodeText: {
    fontSize: '12px',
    color: '#6b7280'
  },
  dateText: {
    fontSize: '13px',
    color: '#6b7280',
    whiteSpace: 'nowrap'
  },
  deleteButton: {
    padding: '4px 12px',
    backgroundColor: '#fee2e2',
    color: '#ef4444',
    border: '1px solid #fecaca',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  emptyState: {
    padding: '48px',
    textAlign: 'center'
  },
  emptyStateContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px'
  },
  emptyIcon: {
    fontSize: '48px',
    color: '#9ca3af'
  },
  emptyText: {
    fontSize: '16px',
    color: '#6b7280',
    margin: 0
  },
  clearFiltersButton: {
    padding: '8px 16px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer'
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '20px'
  },
  paginationInfo: {
    fontSize: '14px',
    color: '#6b7280'
  },
  paginationControls: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center'
  },
  paginationButton: {
    padding: '6px 12px',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    fontSize: '13px',
    color: '#374151',
    cursor: 'pointer'
  },
  pageIndicator: {
    padding: '6px 12px',
    fontSize: '13px',
    color: '#374151'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '400px',
    maxWidth: '90%',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
  },
  modalHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  modalTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    color: '#9ca3af',
    cursor: 'pointer',
    padding: 0
  },
  modalBody: {
    padding: '20px'
  },
  modalText: {
    fontSize: '14px',
    color: '#374151',
    margin: '0 0 8px 0'
  },
  modalWarning: {
    fontSize: '13px',
    color: '#ef4444',
    margin: 0
  },
  modalFooter: {
    padding: '16px 20px',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px'
  },
  modalCancelButton: {
    padding: '8px 16px',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#374151',
    cursor: 'pointer'
  },
  modalDeleteButton: {
    padding: '8px 16px',
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f3f4f6'
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e5e7eb',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px'
  },
  loadingText: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f3f4f6',
    padding: '20px'
  },
  errorIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  errorTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 8px 0'
  },
  errorMessage: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 20px 0',
    textAlign: 'center'
  },
  retryButton: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  statIconContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
  },
  statIcon: {
    fontSize: '24px',
    color: 'white',
    lineHeight: 1
  },
};

// Add animation keyframes
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default KukoList;